import { Injectable, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { LiveAnnouncer } from '@angular/cdk/a11y';

export const TITLE_SUFFIX = 'Career Path DECIDE';
const SUFFIX = TITLE_SUFFIX;

/**
 * Sets the document title on every navigation, and announces the change.
 *
 * Before this, all 32 routes returned the identical title "Career Path DECIDE".
 * In a single-page application the document title is the primary signal that
 * navigation succeeded — it is the first thing a screen reader announces on
 * load, and what appears in the tab, history and bookmarks. Without it a user
 * moving from the Goals Report to the Interests survey got no confirmation
 * anything had happened (WCAG 2.4.2).
 *
 * Setting the title alone is not enough in an SPA: the document does not
 * reload, so nothing re-reads it. The LiveAnnouncer call is what actually makes
 * the change perceivable (WCAG 4.1.3). Focus is moved to <main> separately in
 * AppComponent, which together give the user the same "you are somewhere new"
 * cue a full page load provides.
 *
 * Routes without an explicit `title` fall back to the product name rather than
 * keeping the previous page's title, which would be actively misleading.
 */
@Injectable({ providedIn: 'root' })
export class CpdTitleStrategy extends TitleStrategy {
  private readonly title = inject(Title);
  private readonly announcer = inject(LiveAnnouncer);

  public override updateTitle(snapshot: RouterStateSnapshot): void {
    const page = this.buildTitle(snapshot);
    const full = page ? `${page} · ${SUFFIX}` : SUFFIX;

    // Skip the announcement on first load: the browser reads the title itself,
    // and announcing it again is duplicate noise.
    const isFirstLoad = !this.title.getTitle() || this.title.getTitle() === SUFFIX;

    this.title.setTitle(full);

    if (!isFirstLoad && page) {
      this.announcer.announce(`${page} page loaded`, 'polite');
    }
  }
}
